import { useState } from 'react';
import { t } from '@/lib/i18n';
import { useUploadAttachment } from '@/features/attachments';
import type { Attachment } from '@/features/attachments';
import styles from './ChatAttachmentPicker.module.css';

export interface PendingAttachment {
  localId: string;
  name: string;
  size: number;
  attachment?: Attachment;
  error?: string;
}

interface ChatAttachmentPickerProps {
  attachments: PendingAttachment[];
  onChange: (updater: (current: PendingAttachment[]) => PendingAttachment[]) => void;
}

export function ChatAttachmentPicker({ attachments, onChange }: ChatAttachmentPickerProps) {
  const upload = useUploadAttachment({ embedSync: true });
  const [error, setError] = useState<string | null>(null);

  function handleFiles(filesList: FileList | null) {
    if (!filesList) return;
    setError(null);
    const files = Array.from(filesList);
    const additions: PendingAttachment[] = files.map((f) => ({
      localId: crypto.randomUUID(),
      name: f.name,
      size: f.size,
    }));
    onChange((current) => [...current, ...additions]);

    // Upload each in parallel.
    additions.forEach((p, idx) => {
      const file = files[idx];
      if (!file) return;
      upload
        .mutateAsync({ file })
        .then((res) => {
          onChange((current) =>
            current.map((c) =>
              c.localId === p.localId ? { ...c, attachment: res.attachment } : c,
            ),
          );
        })
        .catch((err) => {
          onChange((current) =>
            current.map((c) =>
              c.localId === p.localId
                ? { ...c, error: err instanceof Error ? err.message : 'Upload failed' }
                : c,
            ),
          );
        });
    });
  }

  function remove(localId: string) {
    onChange((current) => current.filter((p) => p.localId !== localId));
  }

  return (
    <div className={styles.picker}>
      <label className={styles.button}>
        <input
          type="file"
          multiple
          className={styles.input}
          onChange={(e) => handleFiles(e.target.files)}
        />
        {t('Add Files')}
      </label>
      {attachments.length > 0 ? (
        <ul className={styles.chips}>
          {attachments.map((p) => (
            <li key={p.localId} className={styles.chip}>
              <span className={styles.chipName}>{p.name}</span>
              {p.error ? (
                <span className={styles.chipError} title={p.error}>
                  !
                </span>
              ) : !p.attachment ? (
                <span className={styles.chipSpinner} aria-label={t('Uploading')}>
                  …
                </span>
              ) : (
                <span aria-hidden="true">✓</span>
              )}
              <button type="button" className={styles.chipRemove} onClick={() => remove(p.localId)}>
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {error ? <div className={styles.error}>{error}</div> : null}
    </div>
  );
}
